import type {
  InventoryClassificationSnapshot,
  RetailInventoryLevel,
  RetailProduct,
} from "@/features/retail/types/datasets";

export type KitchenInventoryRow = {
  level: RetailInventoryLevel;
  product: RetailProduct | null;
  classification: InventoryClassificationSnapshot;
  available: number;
  onOrder: number;
  reorderPoint: number | null;
  status: "healthy" | "low" | "out";
};

export const deriveKitchenStatus = (
  available: number,
  reorderPoint: number | null,
  safetyStock: number | null,
): KitchenInventoryRow["status"] => {
  if (!Number.isFinite(available) || available <= 0) {
    return "out";
  }
  const threshold = reorderPoint ?? safetyStock;
  if (threshold !== null && Number.isFinite(threshold) && available <= threshold) {
    return "low";
  }
  return "healthy";
};

export const buildKitchenRows = (
  inventory: RetailInventoryLevel[],
  catalog: RetailProduct[],
): KitchenInventoryRow[] => {
  const productById = new Map<string, RetailProduct>();
  for (const product of catalog) {
    productById.set(product.id, product);
  }

  const rows: KitchenInventoryRow[] = [];

  for (const level of inventory) {
    if (!level.classification || level.classification.kind === "retail_product") {
      continue;
    }
    const product = productById.get(level.productId) ?? null;
    const available = Math.max(0, (level.onHand ?? 0) - (level.allocated ?? 0));
    const reorderPoint = level.reorderPoint ?? product?.reorderPoint ?? null;
    const safetyStock = level.safetyStock ?? product?.safetyStock ?? null;
    const status = deriveKitchenStatus(available, reorderPoint, safetyStock);

    rows.push({
      level,
      product,
      classification: level.classification,
      available,
      onOrder: level.onOrder ?? 0,
      reorderPoint,
      status,
    });
  }

  return rows;
};
